/**
 * Modul-Registry — registriert alle verfuegbaren App-Module.
 *
 * Zwei Register-Wege:
 *  a) STATISCH: Module die noch im Portal-Bundle leben werden per Top-Level-
 *     Import geladen und synchron registriert (bauberichte, disposition, vorlagen).
 *  b) FEDERATION (Self-Healing seit 2026-08-02): Modul-Manifeste kommen aus
 *     `apps/<slug>/frontend`-Containern. Werden asynchron beim Boot geladen,
 *     fehlende werden alle 30s neu probiert (Container-Restart erkennen).
 *
 * Self-Healing-Strategie:
 *  1. `initModules()` versucht alle 11 Remotes per HEAD-Check + `import()`.
 *  2. Verfuegbare: `registerModule()` + Event `federation-module-registered`.
 *  3. Fehlende: bleiben in `pendingRemotes`-Set. Retry-Loop `setInterval(30s)`
 *     versucht sie neu. Bei Erfolg gleicher Ablauf, dann aus Set entfernen.
 *  4. Sidebar / Router lauschen auf das Event und rerendern.
 *
 * WICHTIG: Alle Cross-Modul-Imports MUESSEN `useFederatedModule` benutzen —
 * KEINE statischen `import { X } from 'bauberichteV2/public'`. Sonst zwingt
 * der @module-federation/vite-Plugin den Bootstrap-Preload aller Remotes, was
 * einen einzelnen Container-Ausfall zum globalen Portal-Boot-Kill macht.
 * Regel: [[feedback_federation_no_static_imports]].
 */
import type { AppModuleManifest } from './types';
// Runtime-Federation (rf9, 2026-08-17): top-level import statt `await import()`.
// Dynamischer Import in einer async-Init-Kette hat Rollup so umsortiert, dass
// der react-draggable-CJS-Interop kaputt ging ($r is not defined). Als
// top-level static import bleibt die Chunk-Ordering deterministisch, weil
// Rollup den Import als reguläre statische Kante behandelt.
// Benutzt fuer registerDynamicRemote()/loadDynamicRemotes() — API ist die
// gleiche @module-federation/runtime v2.8.1 die @module-federation/vite bereits
// zur Init nutzt (transitive dep), also keine zweite Runtime-Instanz.
import { registerRemotes, loadRemote } from '@module-federation/runtime';

const MODULE_REGISTRY = new Map<string, AppModuleManifest>();

// Status-Tracking (2026-08-02): Sidebar zeigt gruene/rote Punkte pro Modul.
// 'up' = Federation-Remote geladen ODER statisches Portal-Bundle-Modul (immer up).
// 'down' = HEAD-Check oder import() gescheitert.
// 'unknown' = noch nicht probiert (Portal boot-Phase, vor initModules).
export type ModuleStatus = 'up' | 'down' | 'unknown';
const moduleStatus = new Map<string, ModuleStatus>();

/** Status setzen + Event feuern wenn geaendert */
function setModuleStatus(slug: string, status: ModuleStatus): void {
  const prev = moduleStatus.get(slug);
  if (prev === status) return;
  moduleStatus.set(slug, status);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('federation-status-change', { detail: { slug, status } })
    );
  }
}

/** Status lesen (fuer useFederatedStatus-Hook) */
export function getModuleStatus(slug: string): ModuleStatus {
  return moduleStatus.get(slug) ?? 'unknown';
}

/** Modul registrieren + Status 'up' + Event feuern (Consumer koennen darauf horchen) */
export function registerModule(manifest: AppModuleManifest): void {
  const wasNew = !MODULE_REGISTRY.has(manifest.slug);
  MODULE_REGISTRY.set(manifest.slug, manifest);
  setModuleStatus(manifest.slug, 'up');
  if (wasNew && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('federation-module-registered', { detail: { slug: manifest.slug } })
    );
  }
}

/** Modul nach Slug holen */
export function getModule(slug: string): AppModuleManifest | undefined {
  return MODULE_REGISTRY.get(slug);
}

/** Alle registrierten Module */
export function getAllModules(): AppModuleManifest[] {
  return Array.from(MODULE_REGISTRY.values());
}

// ========== Statische Modul-Registrierung (Portal-Bundle) ==========
// Joerg 2026-08-31: Legacy-V1-Modul `bauberichte` (/m/bauberichte, Seite
// „Sprache") deregistriert — es konnte Berichte ohne Aufgabe/Kunde erzeugen
// und ist seit Phase 2a durch bauberichteV2 ersetzt. Der Ordner
// `modules/bauberichte/` bleibt vorerst als toter Code (Aufraeum-Fund folgt);
// das V1-BACKEND bleibt, disposition nutzt dessen API weiter.

import dispositionManifest from './disposition/manifest';
registerModule(dispositionManifest);

import vorlagenManifest from './vorlagen/manifest';
registerModule(vorlagenManifest);


// ========== Federation-Remotes (asynchron + self-healing) ==========
type RemoteEntry = {
  url: string;
  loader: () => Promise<{ default: AppModuleManifest }>;
};

const REMOTES: Record<string, RemoteEntry> = {
  ressourcen: {
    url: '/modules/ressourcen/remoteEntry.js',
    loader: () => import('ressourcen/manifest'),
  },
  fuhrpark: {
    url: '/modules/fuhrpark/remoteEntry.js',
    loader: () => import('fuhrpark/manifest'),
  },
  'service-desk': {
    url: '/modules/service-desk/remoteEntry.js',
    loader: () => import('service-desk/manifest'),
  },
  bibliothek: {
    url: '/modules/bibliothek/remoteEntry.js',
    loader: () => import('bibliothek/manifest'),
  },
  stammdaten: {
    url: '/modules/stammdaten/remoteEntry.js',
    loader: () => import('stammdaten/manifest'),
  },
  chat: {
    url: '/modules/chat/remoteEntry.js',
    loader: () => import('chat/manifest'),
  },
  dms: {
    url: '/modules/dms/remoteEntry.js',
    loader: () => import('dms/manifest'),
  },
  'onvi-capture': {
    url: '/modules/onvi-capture/remoteEntry.js',
    loader: () => import('onvi-capture/manifest'),
  },
  bauberichteV2: {
    url: '/modules/bauberichteV2/remoteEntry.js',
    loader: () => import('bauberichteV2/manifest'),
  },
  zeiterfassung: {
    url: '/modules/zeiterfassung/remoteEntry.js',
    loader: () => import('zeiterfassung/manifest'),
  },
  plantafel: {
    url: '/modules/plantafel/remoteEntry.js',
    loader: () => import('plantafel/manifest'),
  },
};

// ========== Dynamische Federation-Remotes (rf9, 2026-08-17) ==========
// Runtime-Registration via @module-federation/runtime — kein Portal-Rebuild
// bei neuen Modulen. Der Loader ruft loadRemote('slug/manifest'), was der
// registerRemotes()-Aufruf zuvor an die bereits initialisierte Runtime
// gehaengt hat.
//
// Fluss fuer ein neues Modul (Kiara-Skelett-Standard):
//   1. Modul-Container startet, self-registry.ts schreibt Zeile in
//      portal_module_registry (mit nav_route + nav_label + entry-URL).
//   2. Nach Login fetched das Portal /api/portal/modules/available.
//   3. Fuer jeden Slug der nicht in REMOTES ist: registerDynamicRemote(slug,url).
//   4. initDynamicRemotes() ruft tryLoadDynamicRemote() → loadRemote() →
//      registerModule(manifest). Sidebar horcht auf federation-module-registered.
const DYNAMIC_REMOTES = new Map<string, { url: string }>();

/**
 * Ein neues Federation-Modul zur Laufzeit registrieren.
 * Idempotent — mehrfacher Aufruf mit gleichem Slug ist no-op.
 * Ignoriert wenn der Slug bereits statisch in REMOTES steht.
 */
export function registerDynamicRemote(slug: string, url: string): void {
  if (REMOTES[slug]) return;
  // Bundle-statische Module (z.B. vorlagen — oben per Import registriert)
  // sind KEINE Federation-Remotes. Ihre Registry-container_url dient nur dem
  // API-Proxy (der Assistant registriert 'vorlagen' fuer /api/modules/
  // vorlagen/*). Ohne diesen Guard versucht der Loader ihr nicht
  // existentes remoteEntry.js und markiert das gesunde Modul 'down' —
  // roter Punkt in der Sidebar (Jörg-Fund DEB02 2026-09-02; gleiche
  // Klasse wie der adressbuch-Fall bc97839d, nur von der Modul-Seite
  // registriert statt portal-intern und daher nicht im Backend-Filter).
  if (getModule(slug)) return;
  if (DYNAMIC_REMOTES.has(slug)) return;
  DYNAMIC_REMOTES.set(slug, { url });
  registerRemotes([{ name: slug, entry: url, type: 'module' }]);
}

/** HEAD-Check + loadRemote + registerModule fuer einen dynamischen Remote. */
async function tryLoadDynamicRemote(name: string, url: string, budgetMs = RETRY_INIT_BUDGET_MS): Promise<boolean> {
  const available = await isRemoteAvailable(url);
  if (!available) {
    setModuleStatus(name, 'down');
    return false;
  }
  try {
    const mod = await loadWithAsyncInitRetry(
      () => loadRemote<{ default: AppModuleManifest }>(`${name}/manifest`),
      budgetMs,
    );
    if (!mod || !mod.default) {
      console.warn(`[Registry] loadRemote('${name}/manifest') returned null/empty`);
      setModuleStatus(name, 'down');
      return false;
    }
    registerModule(mod.default);
    return true;
  } catch (err) {
    console.warn(`[Registry] Dynamic "${name}" import gescheitert:`, (err as Error)?.message ?? err);
    setModuleStatus(name, 'down');
    return false;
  }
}

const pendingDynamicRemotes = new Set<string>();

/**
 * Batch-Init: alle uebergebenen dynamischen Remotes registrieren + laden.
 * Nach Login vom AuthEffects-Hook aufgerufen mit der Liste aus
 * /api/portal/modules/available. Idempotent — bereits geladene bleiben.
 */
export async function initDynamicRemotes(
  remotes: Array<{ slug: string; url: string }>
): Promise<void> {
  for (const r of remotes) registerDynamicRemote(r.slug, r.url);
  const active = Array.from(DYNAMIC_REMOTES.entries());
  const results = await Promise.allSettled(
    active.map(async ([name, { url }]) => {
      const ok = await tryLoadDynamicRemote(name, url, BOOT_INIT_BUDGET_MS);
      if (!ok) pendingDynamicRemotes.add(name);
      else pendingDynamicRemotes.delete(name);
      return { name, ok };
    })
  );
  const failed = results
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((v): v is { name: string; ok: boolean } => !!v && !v.ok)
    .map((v) => v.name);
  if (failed.length > 0) {
    console.warn(`[Registry] ${failed.length} dynamische Remotes initial down:`, failed);
    startRetryLoop(); // gleicher Loop wie fuer statische Remotes
  }
}

/**
 * HEAD-Check mit Timeout — verhindert Boot-Verzoegerung bei haengendem Container.
 * 2026-08-20: 3s → 8s. Direkt nach einem Portal-Recreate ist das Backend kalt
 * (DB-Pool, Modul-Registry-Lookup pro Proxy-Request) und 11 parallele HEADs
 * konkurrieren mit allen Boot-API-Calls um die 6 Browser-Connections pro Host.
 * Mit 3s rutschten die letzten HEADs der Queue regelmaessig in den Abort →
 * falscher roter Punkt fuer eigentlich gesunde Container. initModules() ist
 * fire-and-forget, der laengere Timeout blockiert also keinen Render.
 */
async function isRemoteAvailable(url: string): Promise<boolean> {
  // PWA-Offline (Jörg-Fund 2026-09-09, Teil 3): offline meldete der HEAD-Check
  // ALLE Module als down — die gecachten remoteEntries wurden nie geladen,
  // jede Modul-Route hing im Spinner. Ohne Netz entscheidet der SW-Cache:
  // optimistisch laden, der Script-Load faellt auf den Cache zurueck.
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 8000);
  try {
    const res = await fetch(url, { method: 'HEAD', signal: ctrl.signal });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

/**
 * Federation-Load mit Async-Init-Toleranz (2026-08-21).
 *
 * Die @module-federation/vite-Bridge wirft bei einer noch laufenden Share-/
 * Remote-Initialisierung DIE PENDING PROMISE SELBST als "Fehler" (Suspense-
 * Muster, gedacht fuer React-Boundaries). Trifft deterministisch die Module,
 * die selbst Cross-Remote-Abhaengigkeiten haben (zeiterfassung + plantafel
 * laden intern bauberichteV2/public): verliert deren Init das Boot-Rennen
 * gegen bauberichteV2, rejected der import() mit einer FULFILLED Promise —
 * kein echter Ausfall. Der alte Code wertete das als 'down' (roter Punkt),
 * und der 30s-Retry lief in dasselbe Muster. Live auf staging-meiners per
 * Runtime-Probe bewiesen: Promise abwarten + einmal neu laden → Modul da.
 *
 * WICHTIG: KEIN registerRemotes(..., { force: true }) als Heilversuch — das
 * ueberschreibt die Plugin-interne Remote-Registrierung und erzeugt
 * RUNTIME-004 "Failed to locate remote" (auf staging-meiners nachgestellt).
 */
// Budgets (Empirie 2026-08-21, alles live gemessen): Die Suspense-Konvergenz
// wartet real auf parallele Container-Loads (mehrere Sekunden, bauberichteV2
// kalt) — 2,5s reichten auf staging-meiners nicht, 20s ja. Seit main.tsx den
// Render NICHT mehr hinter initModules() haengt (2026-08-21), blockiert ein
// langes Boot-Budget nichts mehr — beide Budgets duerfen grosszuegig sein.
// Die Unterscheidung bleibt als Stellschraube erhalten.
const BOOT_INIT_BUDGET_MS = 20_000;
const RETRY_INIT_BUDGET_MS = 20_000;

// Exportiert seit 2026-08-21 (FED-01 Modul-Fix): auch die Host-Bruecke
// (useFederatedModule.ts::loadFederatedModule) treibt ihre Loads durch diese
// Schleife — ein nackter import() rejected sonst mit der geworfenen
// Suspense-Promise, wenn die bauberichteV2-Init gerade laeuft.
export async function loadWithAsyncInitRetry<T>(loader: () => Promise<T>, budgetMs: number): Promise<T> {
  // Suspense-Treiber-Schleife: Die Bridge wirft pro Aufruf die jeweils
  // NAECHSTE pending Init-Promise (verschachtelte loadShare-Stufen). Jede
  // muss awaited werden, damit die Kette weiterlaeuft — ein einzelner Retry
  // reicht nicht (live auf staging-meiners nachgestellt). Cap gegen Endlos-
  // Schleife, falls eine Promise ohne Fortschritt aufloest.
  // Empirie staging-meiners/probst 2026-08-21: Der WIEDERHOLTE loader()-Aufruf
  // selbst treibt die Init-Kette voran — jeder Aufruf stoesst die naechste
  // Share-/Remote-Stufe an (Suspense: werfen → warten → NEU AUFRUFEN).
  // Kuenstliche Delays drosseln die Aufruf-Rate und lassen die Kette
  // verhungern (v2-Test: beide Hosts am Cap gescheitert). Deshalb: schnelles
  // Loopen mit setTimeout(0)-Task-Yield (gibt parallelen Loads wie
  // bauberichteV2 Event-Loop-Zeit ohne zu bremsen).
  //
  // Budget bewusst KURZ (2,5s statt initial 20s): Konvergenz passiert in
  // Millisekunden ueber viele schnelle Zyklen — oder nie (Login-Seite vor
  // Auth). Ein langes Budget heilt nichts zusaetzlich, blockiert aber den
  // Portal-Boot fuer die volle Dauer (schwarzes Fenster bis Budget-Ende,
  // live nachgestellt: Render exakt bei t=Budget). 2,5s entspricht der
  // Groessenordnung des alten 3s-HEAD-Timeouts; danach greift wie gehabt
  // der 30s-Retry-Loop im Hintergrund.
  const deadline = Date.now() + budgetMs;
  // Diagnose-Zaehler (2026-08-21): beim Budget-Ende loggen wir Zyklenzahl +
  // Gestalt des zuletzt aufgeloesten Suspense-Wurfs — noetig um den
  // Meiners-Dauerklemmer einzukreisen (Kette konvergiert dort nie).
  let zyklen = 0;
  let letzterWurf: unknown;
  while (Date.now() < deadline) {
    zyklen++;
    try {
      return await loader();
    } catch (err) {
      if (err && typeof (err as PromiseLike<unknown>).then === 'function') {
        const resolved = await (err as Promise<unknown>).then(
          (v) => v,
          () => undefined,
        );
        letzterWurf = resolved;
        // Manche Bridge-Wuerfe sind FULFILLED Promises, deren Wert bereits
        // der fertige Modul-Namespace ist — dann direkt verwenden.
        if (
          resolved &&
          typeof resolved === 'object' &&
          'default' in (resolved as Record<string, unknown>)
        ) {
          return resolved as T;
        }
        await new Promise((r) => setTimeout(r, 0)); // Task-Yield, kein Delay
        continue;
      }
      throw err;
    }
  }
  const w = letzterWurf as Record<string, unknown> | null | undefined;
  console.warn('[Registry] Suspense-Diagnose:', JSON.stringify({
    zyklen,
    wurfTyp: typeof w,
    ctor: w && typeof w === 'object' ? w.constructor?.name : null,
    keys: w && typeof w === 'object' ? Object.keys(w).slice(0, 12) : null,
    preview: String(w).slice(0, 120),
  }));
  throw new Error(`Federation-Init nach ${budgetMs}ms Suspense-Zyklen nicht abgeschlossen`);
}

/**
 * Ein Remote laden — HEAD-Pre-Check + import() + registerModule.
 * Returned true wenn erfolgreich registriert, false sonst.
 */
async function tryLoadRemote(name: string, entry: RemoteEntry, budgetMs = RETRY_INIT_BUDGET_MS): Promise<boolean> {
  const available = await isRemoteAvailable(entry.url);
  if (!available) {
    setModuleStatus(name, 'down');
    return false;
  }
  try {
    const mod = await loadWithAsyncInitRetry(entry.loader, budgetMs);
    registerModule(mod.default); // setzt intern status='up'
    return true;
  } catch (err) {
    console.warn(`[Registry] "${name}" import gescheitert trotz OK-HEAD:`, (err as Error)?.message ?? err);
    setModuleStatus(name, 'down');
    return false;
  }
}

// Set der noch fehlenden Remotes (die der Retry-Loop weiter probiert)
const pendingRemotes = new Set<string>(Object.keys(REMOTES));
let retryHandle: ReturnType<typeof setInterval> | null = null;

/** Retry-Loop starten — probiert alle 30s die noch fehlenden Remotes neu. Deckt
 *  seit rf9 auch dynamisch registrierte Remotes ab (pendingDynamicRemotes). */
function startRetryLoop(): void {
  if (retryHandle) return;
  retryHandle = setInterval(async () => {
    if (pendingRemotes.size === 0 && pendingDynamicRemotes.size === 0) {
      stopModuleRetry();
      return;
    }
    const staticNames = Array.from(pendingRemotes);
    const dynamicNames = Array.from(pendingDynamicRemotes);
    const staticResults = await Promise.allSettled(
      staticNames.map((name) => tryLoadRemote(name, REMOTES[name]!))
    );
    staticResults.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value === true) {
        pendingRemotes.delete(staticNames[i]!);
        console.info(`[Registry] Remote "${staticNames[i]}" nach Retry erfolgreich geladen`);
      }
    });
    const dynResults = await Promise.allSettled(
      dynamicNames.map((name) => {
        const entry = DYNAMIC_REMOTES.get(name);
        if (!entry) return Promise.resolve(false);
        return tryLoadDynamicRemote(name, entry.url);
      })
    );
    dynResults.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value === true) {
        pendingDynamicRemotes.delete(dynamicNames[i]!);
        console.info(`[Registry] Dynamic Remote "${dynamicNames[i]}" nach Retry erfolgreich geladen`);
      }
    });
  }, 30_000);
}

/** Retry-Loop stoppen (fuer Logout, Test-Teardown, oder wenn alle Remotes da sind) */
export function stopModuleRetry(): void {
  if (retryHandle) {
    clearInterval(retryHandle);
    retryHandle = null;
  }
}

/**
 * Externer Trigger — einen einzelnen Remote sofort neu probieren.
 * Vom SSE-Client (federation-status-stream.ts) aufgerufen, sobald der
 * Backend-Watchdog meldet, dass ein zuvor down-er Container wieder up ist.
 *
 * Ohne diesen Trigger muesste der Consumer bis zum naechsten 30s-Poll warten.
 * Returned true wenn der Remote nun geladen ist, false sonst.
 */
export async function retryRemote(name: string): Promise<boolean> {
  const entry = REMOTES[name];
  if (entry) {
    const ok = await tryLoadRemote(name, entry);
    if (ok) pendingRemotes.delete(name);
    return ok;
  }
  const dyn = DYNAMIC_REMOTES.get(name);
  if (dyn) {
    const ok = await tryLoadDynamicRemote(name, dyn.url);
    if (ok) pendingDynamicRemotes.delete(name);
    return ok;
  }
  return false;
}

/**
 * Externer Trigger — einen Slug als down markieren.
 * Vom SSE-Client aufgerufen wenn der Backend-Watchdog einen Ausfall meldet.
 * Feuert `federation-status-change`-Event; Sidebar-Punkt geht rot.
 */
export function markRemoteDown(name: string): void {
  const isStatic = !!REMOTES[name];
  const isDynamic = DYNAMIC_REMOTES.has(name);
  if (!isStatic && !isDynamic) return;
  setModuleStatus(name, 'down');
  if (isStatic) pendingRemotes.add(name);
  if (isDynamic) pendingDynamicRemotes.add(name);
  // Retry-Loop starten, falls er schon gestoppt war (alle Remotes waren mal up).
  if (!retryHandle) startRetryLoop();
}

/** Initial-Load aller Remotes + Retry-Loop starten. Idempotent. */
let modulesReady: Promise<void> | null = null;
export function initModules(): Promise<void> {
  if (modulesReady) return modulesReady;
  modulesReady = (async () => {
    const results = await Promise.allSettled(
      Object.entries(REMOTES).map(async ([name, entry]) => {
        const ok = await tryLoadRemote(name, entry, BOOT_INIT_BUDGET_MS);
        if (ok) pendingRemotes.delete(name);
        return { name, ok };
      })
    );
    const failed = results
      .map((r) => (r.status === 'fulfilled' ? r.value : null))
      .filter((v): v is { name: string; ok: boolean } => !!v && !v.ok)
      .map((v) => v.name);
    if (failed.length > 0) {
      console.warn(`[Registry] ${failed.length} Federation-Remotes fehlen initial:`, failed);
      startRetryLoop();
    }
  })();
  return modulesReady;
}
